// /* eslint-disable react-hooks/rules-of-hooks */
import { chatChannels } from '@/constants/chatHistory';
import { StyledButton } from '@/constants/themes';
import useAlchemystStoreForAi from '@/hooks/ai/client/useAlchemystStoreForAi';
import { ChatHistoryContainerProps } from '@/types/components/converse/history';
import Add from '@mui/icons-material/Add';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import ChatHistoryList from './ChatHistoryList';
import ChatHistorySkeleton from './ChatHistorySkeleton';
import { useCallback, useEffect, useState } from 'react';
import { ChatHistoryEntryType } from '@/types/converse/chatHistory';
import { createTimeoutSignal } from '@/utils/signalConfig';
import { fetchWithRewrites } from '@/utils/fetchWithRewrites';

/**
 * ChatHistoryContainer component
 *
 * This component displays the chat history and a "New Chat" button to start a new chat session.
 * If loading, it shows a skeleton loader to indicate data is being fetched.
 * Otherwise, it renders the chat history list and the button to initiate a new chat.
 *
 * @param loading - Boolean to determine if the skeleton loader should display
 * @param setCurrentChatID - Callback to set the current chat ID; used to start a new or existing chat
 * @param props - Additional properties passed down to the ChatHistoryList component
 */

function ChatHistoryContainer({
  loading,
  setCurrentChatID,
  ...props
}: ChatHistoryContainerProps): React.ReactNode {
  const { chatChannel, previousChannel, setStoreState, chat } =
    useAlchemystStoreForAi((store) => ({
      chatChannel: store.chatChannel,
      previousChannel: store.previousChannel,
      setStoreState: store.setStoreState,
      chat: store.chat,
    }));
  const setChatChannel = (chatChannelName: string): void => {
    setStoreState({ chatChannel: chatChannelName });
  };

  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  const setChatHistory = (chats: ChatHistoryEntryType[]): void => {
    setStoreState({ chatHistory: chats });
  };
  const chatHistory = useAlchemystStoreForAi((store) => store.chatHistory);

  const filterDuplicateUntitledChats = (
    chats: ChatHistoryEntryType[]
  ): ChatHistoryEntryType[] => {
    const untitledChats: ChatHistoryEntryType[] = [];
    const normalChats: ChatHistoryEntryType[] = [];

    chats.forEach((chat) => {
      if (chat.title.toLowerCase().includes('untitled chat at')) {
        untitledChats.push(chat);
      } else {
        normalChats.push(chat);
      }
    });

    let mostRecentUntitledChat: ChatHistoryEntryType | null = null;

    if (untitledChats.length > 0) {
      mostRecentUntitledChat = untitledChats.reduce((latest, current) =>
        current.timestamp > latest.timestamp ? current : latest
      );
    }

    return mostRecentUntitledChat
      ? [...normalChats, mostRecentUntitledChat]
      : normalChats;
  };

  useEffect(() => {
    fetchHistory({ channel: true }, 1);
  }, [chatChannel]);

  const fetchNextPage = () => {
    fetchHistory({ channel: false }, page + 1);
  };

  const fetchHistory = useCallback(
    async (options: { channel: boolean }, pageToFetch = 1) => {
      if (!hasMore && !options.channel) return;

      try {
        const chatHistoryResponse = await fetchWithRewrites(
          `/api/v1/chat/history?sources=${chatChannel}&page=${pageToFetch}&limit=10`,
          {
            signal: createTimeoutSignal(),
          }
        );
        const chatHistoryJson: {
          chats: ChatHistoryEntryType[];
          pagination: {
            page: number;
            limit: number;
            hasMore: boolean;
            total: number;
          };
          pinnedCount: number;
        } = await chatHistoryResponse.json();

        console.log('Received chat history from /api/v1/chat/history = ');
        console.log(chatHistoryJson.chats ?? []);

        const filteredChats = filterDuplicateUntitledChats(
          chatHistoryJson.chats ?? []
        );

        setStoreState({
          chatHistory: options.channel
            ? filteredChats
            : [...chatHistory, ...filteredChats],
          pinnedCount: chatHistoryJson.pinnedCount,
        });

        setPage(pageToFetch);
        setHasMore(chatHistoryJson.pagination.hasMore);

        setStoreState({
          previousChannel: chatChannel,
        });
      } catch (error) {
        console.error('Failed to fetch chat history:', error);
        if (pageToFetch === 1) {
          setChatHistory([]);
        }
        setHasMore(false);
      }
    },
    [chatChannel, chatHistory, hasMore]
  );

  if (loading) {
    return <ChatHistorySkeleton skeletonCount={10} />;
  }

  return (
    <Box
      sx={{
        paddingX: 2,
        height: '100vh',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <Typography
        sx={{
          marginBottom: 3,
        }}
        variant="h5"
      >
        Chat History
      </Typography>

      <Stack spacing={2} sx={{ flex: 1, minHeight: 0 }}>
        <Stack spacing={2}>
          <StyledButton
            color="primary"
            onClick={() => {
              setCurrentChatID('');
              setStoreState({
                chat: {
                  ...chat,
                  tools: { ...chat.tools, researchIcps: false },
                },
                chatRecordings: {},
              });
            }}
            startIcon={<Add />}
            sx={{
              borderRadius: 1,
              backgroundColor: '#EAEAEA',
              color: 'black',
              boxShadow: 3,
              '&:hover': {
                backgroundColor: '#EAEAEA',
              },
            }}
            variant="contained"
          >
            New Chat
          </StyledButton>
          <Box sx={{ minWidth: 120 }}>
            <FormControl fullWidth>
              <InputLabel id="demo-simple-select-label">Option</InputLabel>
              <Select
                id="platform-select"
                label="Option"
                labelId="platform-select-label"
                onChange={(e) => {
                  setChatChannel(e.target.value);
                }}
                value={chatChannel}
                sx={{
                  '& .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#EAEAEA',
                  },
                  '&:hover .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#EAEAEA',
                  },
                  '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                    borderColor: '#EAEAEA',
                  },
                  '& .MuiInputBase-root': {
                    '&.Mui-focused': {
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#EAEAEA',
                      },
                    },
                  },
                }}
              >
                {chatChannels.map((channel) => (
                  <MenuItem
                    key={`channel-${channel.channel}`}
                    value={channel.channel}
                  >
                    {channel.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
        </Stack>
        <Divider />
        <Box
          sx={{
            flex: 1,
            overflowY: 'auto',
            py: 1,
            minHeight: 0,
            msOverflowStyle: 'none', // Hide scrollbar for IE and Edge
            scrollbarWidth: 'none', // Hide scrollbar for Firefox
            '&::-webkit-scrollbar': {
              // Hide scrollbar for Chrome, Safari and Opera
              display: 'none',
            },
          }}
        >
          <ChatHistoryList
            {...props}
            fetchNextPage={fetchNextPage}
            hasMore={hasMore}
            setCurrentChatID={setCurrentChatID}
            refetchHistory={(channel, page: number | undefined) =>
              fetchHistory({ channel: true }, 1)
            }
            page={page}
          />
          <Box py={4} />
        </Box>
      </Stack>
    </Box>
  );
}

export default ChatHistoryContainer;
